import {useEffect, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useLocation} from 'react-router-dom'
import {useAccessToken, useCustomerType} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteEnvVarValue} from '../utils/site-config'
import {useBrand, toBrandCode} from '../contexts/brand-context'
import {usePreviewContext} from '../contexts/preview-context'
import {
    getPreviewProjectId,
    applyPreviewBlocks,
    storePreviewData,
    syncPreviewSession
} from '../utils/optimizely-preview'

/* One-shot debug dump of the resolved page payload. Replaces the per-component
 * logs in contentRenderer.jsx so the console gets a single collapsed entry per
 * page fetch instead of dozens of per-render entries. */
const DEBUG_LOG_OPTIMIZELY = true

/**
 * Deep merge preview content into fetched data
 * Finds blocks by ContentLink.GuidValue and replaces with preview version
 * When the preview IS the page itself (ContainerPage), replace the entire page
 */
const mergePreviewContent = (data, previewContent) => {
    if (!data || !previewContent?.item) {
        return data
    }

    const previewItem = previewContent.item
    const previewKey = previewItem?.ContentLink?.GuidValue

    if (!previewKey) {
        return data
    }

    // Check if the preview content IS the page itself (ContainerPage)
    // — both data.item and preview.item have the same GuidValue.
    if (data?.item?.ContentLink?.GuidValue === previewKey) {
        // Replace the entire page with preview content, preserving the `item` shape.
        return {
            ...data,
            item: previewItem
        }
    }

    // Helper to recursively find and replace content by GuidValue
    const replaceContent = (obj) => {
        if (!obj || typeof obj !== 'object') return obj

        // Check if this object has a matching ContentLink.GuidValue
        if (obj?.ContentLink?.GuidValue === previewKey) {
            // Replace the Expanded content with preview content
            return {
                ...obj,
                ContentLink: {
                    ...obj.ContentLink,
                    Expanded: previewItem
                }
            }
        }

        // Recursively process arrays
        if (Array.isArray(obj)) {
            return obj.map((item) => replaceContent(item))
        }

        // Recursively process object properties
        const result = {}
        for (const key of Object.keys(obj)) {
            result[key] = replaceContent(obj[key])
        }
        return result
    }

    return replaceContent(data)
}

export function useOptimizelyPage(customPath) {
    const location = useLocation()
    // Use custom path if provided (e.g., 'header', 'homepage', 'pdp')
    // If customPath is explicitly null, don't fall back to pathname (query will be disabled)
    // If customPath is undefined, fall back to current route pathname
    const address = customPath === null ? null : (customPath || location.pathname)
    const appOrigin = useAppOrigin()
    const {site} = useMultiSite()

    /* Brand + locale drive which Optimizely content tree to hit
     * (`/{locale}/{brandSlug}/`). Both context values are populated
     * synchronously during AppConfig.restore on server + client, so they're
     * safe to read here without breaking SSR/hydration parity. */
    const {brandId} = useBrand()
    const {locale} = useMultiSite()
    const localeId = locale?.id || locale?.alias || 'en-CA'
    const brandSlug = `${toBrandCode(brandId).toLowerCase()}-web`

    /* Shopper identity for server-side A/B bucketing (TSD §4). The SLAS access
     * token is forwarded so the /optimizely-page endpoint can verify it as the
     * auth authority; isRegistered is a cheap login hint. Both are
     * localStorage-backed (reactive). To keep SSR/hydration output identical
     * (SSR always resolves guest — it's a server-to-server fetch), the key reads
     * guest until `hydrated` flips after mount, then re-buckets with the real
     * login state via a normal refetch. */
    const {token: accessToken} = useAccessToken()
    const {isRegistered} = useCustomerType()
    const [hydrated, setHydrated] = useState(false)
    useEffect(() => setHydrated(true), [])
    const effectiveIsRegistered = hydrated ? !!isRegistered : false

    /* Customer Group targeting (TSD §5). The authenticated customer carries its
     * effective group IDs as `c_customerGroups` (exposed by the SCAPI customer
     * GET hook). Forwarded to the endpoint so server-side filterTargeting can
     * match Customer Group blocks. Gated behind `hydrated` for the same
     * SSR/hydration-parity reason as isRegistered (SSR resolves no groups), and
     * normalized to a stable comma-joined string for the query key + header. */
    const {data: customer} = useCurrentCustomer()
    const customerGroupsCsv =
        hydrated && Array.isArray(customer?.c_customerGroups)
            ? customer.c_customerGroups.join(',')
            : ''

    // During SSR, use PWA_DOMAIN_SITE from env (bypasses basic auth issues on MRT)
    const isSSR = typeof window === 'undefined'
    let fetchOrigin = appOrigin
    if (isSSR) {
        const {app: appConfig} = getConfig()
        const pwaDomain = getSiteEnvVarValue(appConfig, 'PWA_DOMAIN_SITE', site?.id)
        if (pwaDomain) {
            const protocol = pwaDomain.startsWith('localhost') ? 'http' : 'https'
            fetchOrigin = `${protocol}://${pwaDomain}`
        }
    }

    // Check if we're in Optimizely preview mode and this matches the preview page
    // identifier. pageIdentifier may be an array (e.g. a GDICartPage feeds both
    // the 'cart' and 'cart-minicart' reads); the actual replace stays GuidValue-
    // gated in mergePreviewContent, so listing extra identifiers is safe.
    const {previewContent, pageIdentifier, isPreviewMode} = usePreviewContext()
    const shouldMergePreview =
        isPreviewMode &&
        (Array.isArray(pageIdentifier)
            ? pageIdentifier.includes(customPath)
            : pageIdentifier === customPath)

    // MRT preview: get project ID from sessionStorage or URL param
    const previewProjectId =
        typeof window !== 'undefined'
            ? getPreviewProjectId() ||
              new URLSearchParams(location.search).get('optimizely_project')
            : null
    const isMrtPreview = !!previewProjectId

    // Preview session (cookie): the single signal for cache-bypass + drafts.
    // ?preview=<password> enters/refreshes it, ?preview=false exits. Once set it
    // persists across navigation/reload, so preview survives even when the URL
    // is just ?optimizely_project=… . The value (password in prod) is threaded
    // onto fetches below since PWA Kit strips the Cookie header server-side.
    const previewValue = typeof window !== 'undefined' ? syncPreviewSession() : null
    const cacheBypassActive = Boolean(previewValue)

    const query = useQuery({
        /* brand + locale partition the cache so the 6 (brand × locale) trees
         * never collide. previewProjectId stays so preview swaps refetch.
         * cacheBypassActive forces React Query to refetch when CMS team toggles. */
        queryKey: [
            'optimizely-page',
            address,
            brandSlug,
            localeId,
            previewProjectId,
            cacheBypassActive,
            effectiveIsRegistered,
            customerGroupsCsv
        ],
        queryFn: async () => {
            const params = new URLSearchParams({
                path: address,
                brand: brandSlug,
                locale: localeId
            })
            if (previewProjectId) {
                params.set('optimizely_project', previewProjectId)
            }
            if (cacheBypassActive) {
                // Forward the real ?preview value (the password in prod) so the
                // server can authorize the cache bypass.
                params.set('preview', previewValue)
            }

            /* Identity rides in HEADERS, never the query string — keeps the token
             * and login state out of URLs, caches and access logs (TSD §4). The
             * endpoint verifies the SLAS token as the auth authority and treats
             * x-is-logged-in only as a fallback hint. */
            const headers = {'x-is-logged-in': effectiveIsRegistered ? 'true' : 'false'}
            if (accessToken) headers.authorization = `Bearer ${accessToken}`
            if (customerGroupsCsv) headers['x-customer-groups'] = customerGroupsCsv

            const res = await fetch(`${fetchOrigin}/optimizely-page?${params}`, {headers})
            const json = await res.json()

            // Handle both old single query format and new combined query format
            let pageData
            if (json?.data?.page) {
                // New combined query format
                pageData = json.data.page
            } else if (json?.data?.Content) {
                // Old single query format
                pageData = json.data
            } else {
                if (process.env.NODE_ENV === 'development') {
                    console.warn('Unknown Optimizely data format')
                }
                pageData = json?.data
            }

            // Apply MRT preview blocks client-side (supplements server-side application)
            if (isMrtPreview && pageData) {
                // Re-fetch fresh blocks from CMS API and update sessionStorage.
                // Pass languageCode (active locale) + siteId so the proxy fetches
                // the right language tree — not the hardcoded en-GB default.
                try {
                    const blocksParams = new URLSearchParams()
                    blocksParams.set('languageCode', localeId || 'en-GB')
                    if (site?.id) blocksParams.set('siteId', site.id)
                    // Forward the preview password so the proxy authorizes in prod.
                    if (previewValue) blocksParams.set('preview', previewValue)
                    const blocksRes = await fetch(
                        `/api/cms-proxy/projects/${previewProjectId}/blocks?${blocksParams}`
                    )
                    if (blocksRes.ok) {
                        const freshData = await blocksRes.json()
                        if (typeof window !== 'undefined') {
                            // Full draft-projects API response (blocks + pages).
                            console.log(`[Preview] projects API response for ${address}:`, freshData)
                        }
                        storePreviewData(freshData, previewProjectId)
                    }
                } catch (e) {
                    console.warn('[Preview] Could not refresh blocks from CMS:', e)
                }
                pageData = applyPreviewBlocks(pageData)
            }

            /* Carry the server-resolved Optimizely decisions onto the returned
             * payload so they survive React Query's SSR hydration — queryFn does
             * NOT re-run on the client after a hydrated/cache-hit load, so the
             * console dump happens in the `query.data` effect below. Attached
             * last so preview-block rebuilds above don't strip it. */
            if (pageData && typeof pageData === 'object' && json?._optimizelyDecisions) {
                pageData._optimizelyDecisions = json._optimizelyDecisions
            }
            if (pageData && typeof pageData === 'object' && json?._optimizelyAllDecisions) {
                pageData._optimizelyAllDecisions = json._optimizelyAllDecisions
            }

            return pageData
        },
        // Bypass client cache when MRT preview is active
        staleTime: isMrtPreview ? 0 : 24 * 60 * 60 * 1000,
        // Keep in cache for 24 hours
        gcTime: 24 * 60 * 60 * 1000,
        /* When the queryKey changes because login state resolved after hydration
         * (guest → registered, see effectiveIsRegistered), keep the previously
         * rendered content on screen while the re-bucket fetch runs instead of
         * dropping to a blank/loading state — otherwise the page visibly blanks
         * after the spinner and re-renders late for logged-in shoppers.
         * (react-query v4 option; v5's equivalent is placeholderData: keepPreviousData.) */
        keepPreviousData: true,
        // Only fetch when we have a valid address (prevents fetching with null customPath)
        enabled: !!address,
        // Refetch on mount/focus when in preview mode to pick up CMS changes
        refetchOnWindowFocus: isMrtPreview,
        refetchOnMount: isMrtPreview,
        refetchOnReconnect: false
    })

    /* Single collapsed console dump per resolved page payload. Driven by
     * `query.data` so it fires on hydrated cache hits too — logging inside
     * `queryFn` would be skipped when React Query rehydrates from a previous
     * fetch (24h `staleTime`) or from SSR. */
    useEffect(() => {
        if (!DEBUG_LOG_OPTIMIZELY || isSSR || !query.data) return
        const typename =
            query.data?.item?.__typename || query.data?.__typename || 'unknown'
        console.groupCollapsed(`📄 Optimizely page — ${address} (${typename})`)
        console.log(query.data)
        console.groupEnd()

        /* Server-resolved Optimizely decisions for this page (threaded onto the
         * payload in queryFn). Logged here, not in queryFn, so it still fires on
         * SSR-hydrated and React Query cache-hit loads. Two separate logs:
         *   1. decisions the SDK returned for THIS page's A/B blocks (event-firing).
         *   2. ALL flags configured on the Optimizely platform (diagnostic snapshot). */
        const decisions = query.data?._optimizelyDecisions
        console.groupCollapsed(`🧪 Optimizely decisions (page) — ${address}`)
        if (decisions && Object.keys(decisions).length > 0) {
            console.table(decisions)
            console.log(decisions)
        } else {
            console.log(
                'No decisions — no A/B block referenced a valid flag:variation VariantId.'
            )
        }
        console.groupEnd()

        const allDecisions = query.data?._optimizelyAllDecisions
        console.groupCollapsed(`🧪 Optimizely decisions (all flags) — ${address}`)
        if (allDecisions && Object.keys(allDecisions).length > 0) {
            console.table(allDecisions)
            console.log(allDecisions)
        } else {
            console.log('No flags configured on the Optimizely platform (or SDK unavailable).')
        }
        console.groupEnd()
    }, [query.data, address, isSSR])

    // If in Optimizely preview mode and this matches the preview identifier, merge preview content
    if (shouldMergePreview && query.data) {
        const mergedData = mergePreviewContent(query.data, previewContent)
        return {
            ...query,
            data: mergedData
        }
    }

    return query
}
